const brandCode = process.env.REACT_APP_PROJECT_CODE || "CK";

// Per-brand content-language sets for Builder translatable fields — each
// brand's real site only supports its own subset (CK: en/ru/ge, no Arabic;
// MEELZ: en/ru/ar, UAE-facing, no Georgian). Previously this whole file
// hardcoded MEELZ's set (en/ru/ar) for every brand — a live bug that served
// a CK admin an Arabic input/preview instead of CK's real Georgian. E4F
// shares CK's Georgian-market set; DEMO defaults to CK's set as a
// reasonable stand-in.
const LANGUAGE_META_BY_CODE = {
    en: { label: "🇬🇧 English", code: "EN", placeholder: (label) => `Enter ${label.toLowerCase()}` },
    ru: { label: "🇷🇺 Русский", code: "RU", placeholder: (label) => `Введите: ${label.toLowerCase()}` },
    ge: { label: "🇬🇪 ქართული", code: "GE", placeholder: (label) => `შეიყვანეთ ${label.toLowerCase()}` },
    ar: { label: "🇦🇪 العربية", code: "AR", placeholder: (label) => `أدخل ${label.toLowerCase()}` },
};

const LANGUAGES_BY_BRAND = {
    CK: ["en", "ru", "ge"],
    E4F: ["en", "ru", "ge"],
    DEMO: ["en", "ru", "ge"],
    MEELZ: ["en", "ru", "ar"],
};

// Fixed fallback order for resolving a translatable value down to a single
// display string — a priority-ordered lookup, not an if/else chain, scoped
// to the active brand's own languages so a CK session never falls back into
// showing Arabic text (or vice versa for MEELZ/Georgian).
const LANGUAGE_PRIORITY = LANGUAGES_BY_BRAND[brandCode] || LANGUAGES_BY_BRAND.CK;

// Language-selector dropdown options — this brand's supported set only.
const LANGUAGE_SELECT_OPTIONS = LANGUAGE_PRIORITY.map((code) => ({
    value: code,
    label: LANGUAGE_META_BY_CODE[code].label,
}));

// Short uppercase code shown next to a translatable field's label (e.g.
// "Tagline (EN)") so the admin always knows which language they're editing.
const LANGUAGE_CODE_LABEL = Object.fromEntries(
    LANGUAGE_PRIORITY.map((code) => [code, LANGUAGE_META_BY_CODE[code].code])
);

// Per-language placeholder text for translatable fields with no value yet —
// hints to the admin in the language they're currently editing, not just English.
const TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE = Object.fromEntries(
    LANGUAGE_PRIORITY.map((code) => [code, LANGUAGE_META_BY_CODE[code].placeholder])
);

// Resolves any field value to a single display string. Plain strings pass
// through unchanged (legacy/non-translatable fields). A translatable-shaped
// object resolves to `lang`'s value if present, else the first language
// present per LANGUAGE_PRIORITY (this brand's languages), else ANY language
// key the value actually has — covers content saved under a language outside
// the active brand's set (e.g. a MEELZ row's "ar" text read back under a CK
// session) so existing data never silently disappears from a preview.
function resolveTranslatableValue(value, lang) {
    if (value == null || typeof value === "string") return value || "";
    if (lang && value[lang]) return value[lang];
    for (const code of LANGUAGE_PRIORITY) {
        if (value[code]) return value[code];
    }
    for (const code of Object.keys(value)) {
        if (value[code]) return value[code];
    }
    return "";
}

// Strict variant for EDITABLE inputs only — returns ONLY `lang`'s own
// value, never falling back to another language's text. Using
// resolveTranslatableValue's fallback here was a real bug: switching the
// language dropdown to a language with no text yet (e.g. English left
// blank while Russian was typed) would silently show the OTHER language's
// text inside an editable field, which the admin could then overwrite
// without realizing it belonged to a different language. Fallback belongs
// only in read-only preview/client-facing rendering, never in an editor.
function getEditableTranslatableValue(value, lang) {
    if (value == null || typeof value === "string") return value || "";
    return value[lang] || "";
}

export {
    LANGUAGE_PRIORITY,
    LANGUAGE_SELECT_OPTIONS,
    LANGUAGE_CODE_LABEL,
    TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE,
    resolveTranslatableValue,
    getEditableTranslatableValue,
};
